import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal, FormGroup, Input, Select, Textarea, Badge, Table, EmptyState, PageHeader } from '../components/UI';

/* ── useCrudPage hook ──────────────────────────────────────────────── */
export function useCrudPage({ service, formFields, getLabel }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = formFields.reduce((acc, f) => ({ ...acc, [f.key]: f.default ?? '' }), {});
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await service.list({ search: search || undefined, limit: 200 });
      setItems(Array.isArray(res) ? res : res.data ?? []);
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (item) => {
    setEditing(item);
    const f = formFields.reduce((acc, field) => ({
      ...acc,
      [field.key]: item[field.key] ?? field.default ?? '',
    }), {});
    setForm(f);
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await service.update(editing.id, form);
        toast.success('Atualizado com sucesso!');
      } else {
        await service.create(form);
        toast.success('Criado com sucesso!');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const del = async (item) => {
    if (!window.confirm(`Deseja remover "${getLabel(item)}"?`)) return;
    try {
      await service.delete(item.id);
      toast.success('Removido!');
      load();
    } catch {
      toast.error('Erro ao remover');
    }
  };

  return {
    items, loading, search, setSearch, modalOpen, setModalOpen,
    editing, form, setForm, saving, save, openCreate, openEdit, del, load,
  };
}

/* ── FieldRenderer ─────────────────────────────────────────────────── */
export function FieldRenderer({ field, form, setForm }) {
  const val = form[field.key] ?? '';
  const set = (v) => setForm(prev => ({ ...prev, [field.key]: v }));

  if (field.type === 'select') return (
    <FormGroup label={field.label} required={field.required}>
      <Select value={val} onChange={e => set(e.target.value)} required={field.required}>
        <option value="">Selecione...</option>
        {(field.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
    </FormGroup>
  );

  if (field.type === 'textarea') return (
    <FormGroup label={field.label}>
      <Textarea value={val} onChange={e => set(e.target.value)} placeholder={field.placeholder} />
    </FormGroup>
  );

  return (
    <FormGroup label={field.label} required={field.required}>
      <Input
        type={field.type || 'text'}
        value={val}
        onChange={e => set(e.target.value)}
        placeholder={field.placeholder}
        required={field.required}
      />
    </FormGroup>
  );
}

/* ── CrudPage component ────────────────────────────────────────────── */
export default function CrudPage({
  title, service, formFields, columns,
  getLabel = (i) => i.name || i.id,
  extraActions,
  headerActions,
  searchPlaceholder = 'Buscar...',
}) {
  const crud = useCrudPage({ service, formFields, getLabel });
  const {
    items, loading, search, setSearch,
    modalOpen, setModalOpen, editing,
    form, setForm, saving, save, openCreate, openEdit, del,
  } = crud;

  // Build columns for Table component (add actions column)
  const tableColumns = [
    ...columns,
    {
      key: '__actions',
      label: '',
      render: (item) => (
        <div className="table-actions">
          {extraActions?.(item, crud)}
          <button className="btn-icon" title="Editar" onClick={() => openEdit(item)}>
            <Pencil size={14} />
          </button>
          {service.delete && (
            <button
              className="btn-icon"
              title="Remover"
              onClick={() => del(item)}
              style={{ color: 'var(--danger)' }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={title}>
        {headerActions}
        <button className="btn btn-primary" onClick={openCreate}>
          <Plus size={16} /> Novo
        </button>
      </PageHeader>

      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <div className="filters-bar" style={{ margin: 0 }}>
              <div className="search-wrap">
                <Search size={16} />
                <input
                  className="search-input"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>{items.length} registros</span>
          </div>

          {!loading && items.length === 0
            ? <EmptyState title={`Nenhum registro em ${title}`} />
            : <Table columns={tableColumns} data={items} loading={loading} />
          }
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Editar ${title}` : `Novo em ${title}`}
      >
        <form onSubmit={save}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {formFields.map((field) => (
              <div key={field.key} style={{ gridColumn: field.full ? '1 / -1' : undefined }}>
                <FieldRenderer field={field} form={form} setForm={setForm} />
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
